import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, MapPin, Clock, Camera, Loader2, UserCheck, RotateCcw, CheckCircle2, FileText, AlertTriangle, Mail, Navigation, Pencil, Briefcase, StickyNote, CalendarDays, Search, Users, ArrowUp, ArrowDown, ChevronsUpDown, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { api, openJobReport, photoSrc } from "@/lib/api";
import { fmtDuration, fmtDate, fmtDateTime, jobTypeLabel, jobKind, todayLocal } from "@/lib/format";
import { DepotPicker, DepotTag } from "@/components/DepotPicker";
import { InvoiceDialog } from "@/components/invoices/InvoiceDialog";
import { InvoiceStatusBadge } from "@/components/invoices/InvoiceStatusBadge";
import { useDepot } from "@/context/DepotContext";
import { useCompany } from "@/context/CompanyContext";
import { JobTypesDialog } from "@/components/JobTypesDialog";

const jobDisplayStatus = (j) => {
  if (j.status === "completed") return { label: "Completed", cls: "bg-stone-500/15 text-stone-500 border-stone-500/30", dot: false };
  if (j.open_entry_id) return { label: "On site", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30", dot: true };
  if ((j.total_seconds || 0) > 0) return { label: "In progress", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30", dot: false };
  return { label: "Not started", cls: "bg-secondary text-muted-foreground border-border", dot: false };
};

const JobBadge = ({ job }) => {
  const s = jobDisplayStatus(job);
  return (
    <span data-testid="status-badge" className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${s.cls}`}>
      {s.dot && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
      {s.label}
    </span>
  );
};

export const JobsTab = ({ focusJobId, createSignal, onFocusHandled }) => {
  const [jobs, setJobs] = useState([]);
  const [subTab, setSubTab] = useState("active");
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sort, setSort] = useState({ key: "completed", dir: "desc" });
  const [scheduled, setScheduled] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [clients, setClients] = useState([]);
  const [gpsFlags, setGpsFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reschedulingId, setReschedulingId] = useState(null);
  const [form, setForm] = useState({ name: "", client: "", address: "", description: "", job_type: "", worker_email: "", scheduled_date: "", depot_id: "" });
  const [team, setTeam] = useState([]);
  const [typesOpen, setTypesOpen] = useState(false);
  const types = useCompany().company?.job_types || [];
  const formType = types.some((t) => t.id === form.job_type) ? form.job_type : types[0]?.id || "";
  const { needsPick } = useDepot();
  const formClients = needsPick ? clients.filter((c) => c.depot_id === form.depot_id) : clients;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTeamMember = (email) => {
    const w = workers.find((x) => x.email === email);
    if (!w || team.some((m) => m.email === email)) return;
    setTeam((t) => [...t, { email: w.email, name: w.name || w.email }]);
  };
  const removeTeamMember = (email) => setTeam((t) => t.filter((m) => m.email !== email));

  const load = async () => {
    setLoading(true);
    try {
      await api.publishDueJobs().catch(() => {});
      const [j, w, s, f, c] = await Promise.all([api.jobs(), api.workers(), api.scheduledJobs().catch(() => []), api.gpsFlags().catch(() => ({})), api.clients().catch(() => [])]);
      setJobs(j); setWorkers(w); setScheduled(s || []); setGpsFlags(f || {});
      setClients((c || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } catch { toast.error("Failed to load jobs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Open the create dialog when triggered from another screen (e.g. Overview "New job").
  useEffect(() => { if (createSignal) setOpen(true); }, [createSignal]);

  // Deep-link: open a specific job when arriving from a notification (?job=<id>).
  useEffect(() => {
    if (focusJobId && jobs.length) {
      const j = jobs.find((x) => x.id === focusJobId);
      if (j) { setSelected(j); onFocusHandled && onFocusHandled(); }
    }
  }, [focusJobId, jobs]);

  const create = async () => {
    if (!form.name || !form.worker_email) { toast.error("Job name and assigned worker are required"); return; }
    if (needsPick && !form.depot_id) { toast.error("Choose a depot for this job"); return; }
    const today = todayLocal();
    const isFuture = form.scheduled_date && form.scheduled_date > today;
    try {
      const job = await api.createJob({ ...form, job_type: formType || undefined, depot_id: form.depot_id || undefined });
      if (team.length && job?.id) { try { await api.setJobTeam(job.id, team); } catch { /* team is best-effort */ } }
      if (isFuture) {
        toast.success(`Job scheduled for ${form.scheduled_date}`, { description: "The crew won't see it until that day." });
      } else {
        let noti = {};
        try { noti = await api.notifyAssignment(form.worker_email, form.name, job?.id); } catch { /* best-effort */ }
        // Notify any extra crew added to the job too.
        team.forEach((m) => { if (m.email) api.notifyAssignment(m.email, form.name, job?.id).catch(() => {}); });
        const crewNote = team.length ? ` + ${team.length} team member${team.length > 1 ? "s" : ""}` : "";
        if (noti.subs === 0) toast.success(`Job created & assigned${crewNote}`, { description: "Crew won't get a push until they open the app and tap “Enable alerts”." });
        else toast.success(`Job created & assigned${crewNote} — crew notified`);
      }
      setOpen(false);
      setForm({ name: "", client: "", address: "", description: "", job_type: "", worker_email: "", scheduled_date: "", depot_id: "" });
      setTeam([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create job"); }
  };

  const cancelScheduled = async (id) => {
    try { await api.deleteScheduledJob(id); toast.success("Scheduled job cancelled"); load(); }
    catch { toast.error("Could not cancel"); }
  };

  // Quick reschedule straight from a job card (no need to open the full editor).
  const quickReschedule = async (job, date) => {
    if ((job.scheduled_date || "") === (date || "")) return;
    setReschedulingId(job.id);
    try {
      const updated = await api.updateJob(job.id, { scheduled_date: date || "" });
      const recipients = new Set();
      if (updated.assigned_email) recipients.add(updated.assigned_email);
      try { (await api.jobTeam(job.id) || []).forEach((m) => m.email && recipients.add(m.email)); } catch { /* best-effort */ }
      recipients.forEach((emailAddr) => {
        if (date) api.notifyJobReschedule(emailAddr, updated.name, date, job.id).catch(() => {});
        else api.notifyJobUpdate(emailAddr, updated.name, job.id).catch(() => {});
      });
      toast.success(date ? `Rescheduled to ${fmtDate(date)}` : "Scheduled date cleared");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reschedule"); }
    finally { setReschedulingId(null); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  const todayStr = todayLocal();
  const activeJobs = jobs.filter((j) => j.status !== "completed");
  const completedJobs = jobs.filter((j) => j.status === "completed");
  const upcoming = activeJobs
    .filter((j) => j.scheduled_date && j.scheduled_date > todayStr)
    .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
  const current = activeJobs.filter((j) => !(j.scheduled_date && j.scheduled_date > todayStr));
  const q = search.trim().toLowerCase();
  const completedClients = Array.from(new Set(completedJobs.map((j) => j.client).filter(Boolean))).sort();
  const completedFiltered = completedJobs.filter((j) => {
    if (q && ![j.name, j.client, jobTypeLabel(j)].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))) return false;
    if (clientFilter !== "all" && (j.client || "") !== clientFilter) return false;
    const day = (j.completed_at || j.updated_at || "").slice(0, 10);
    if (fromDate && (!day || day < fromDate)) return false;
    if (toDate && (!day || day > toDate)) return false;
    return true;
  });
  const filtersActive = q || clientFilter !== "all" || fromDate || toDate;
  const clearFilters = () => { setSearch(""); setClientFilter("all"); setFromDate(""); setToDate(""); };

  // Sorting for the Completed list.
  const sortVal = (j, key) => {
    switch (key) {
      case "job": return (j.name || "").toLowerCase();
      case "client": return (j.client || "").toLowerCase();
      case "address": return (j.address || "").toLowerCase();
      case "assigned": return (j.assigned_name || j.assigned_email || "").toLowerCase();
      case "completed": return j.completed_at || j.updated_at || "";
      case "time": return j.total_seconds || 0;
      case "photos": return j.photo_count || 0;
      default: return "";
    }
  };
  const toggleSort = (key) => setSort((s) => (
    s.key === key
      ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
      : { key, dir: ["completed", "time", "photos"].includes(key) ? "desc" : "asc" }
  ));
  const completedSorted = [...completedFiltered].sort((a, b) => {
    const va = sortVal(a, sort.key), vb = sortVal(b, sort.key);
    const cmp = (typeof va === "number" && typeof vb === "number") ? va - vb : String(va).localeCompare(String(vb));
    return sort.dir === "asc" ? cmp : -cmp;
  });
  const sortHead = (label, k, align) => (
    <TableHead className={align === "right" ? "text-right" : undefined}>
      <button
        type="button"
        data-testid={`sort-${k}`}
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${align === "right" ? "flex-row-reverse" : ""} ${sort.key === k ? "text-foreground font-semibold" : ""}`}
      >
        {label}
        {sort.key === k
          ? (sort.dir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)
          : <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />}
      </button>
    </TableHead>
  );

  const renderJobCard = (j) => (
    <div key={j.id} data-testid={`job-card-${j.id}`} role="button" tabIndex={0}
      onClick={() => setSelected(j)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(j); } }}
      className="cursor-pointer text-left bg-card border border-border rounded-xl p-5 hover:border-primary/50 hover:shadow-lg transition-all">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-semibold leading-tight">{j.name}</h3>
        <JobBadge job={j} />
      </div>
      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap" data-testid={`job-type-${j.id}`}>{jobTypeLabel(j)}<DepotTag id={j.depot_id} testId={`job-depot-${j.id}`} /></p>
      {j.scheduled_date && (
        <p
          data-testid={`job-scheduled-${j.id}`}
          className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${j.scheduled_date > todayStr ? "bg-amber-500/15 text-amber-600" : "bg-secondary text-muted-foreground"}`}
        >
          <CalendarDays className="w-3.5 h-3.5 shrink-0" /> {fmtDate(j.scheduled_date)}
          {j.scheduled_date > todayStr && <span className="opacity-70">· upcoming</span>}
        </p>
      )}
      {j.client && (
        <p className="text-sm text-muted-foreground mt-3 flex items-center gap-1.5" data-testid={`job-client-${j.id}`}>
          <Briefcase className="w-3.5 h-3.5 shrink-0" /> {j.client}
        </p>
      )}
      {j.address && (
        <p className="text-sm text-muted-foreground mt-1.5 flex items-start gap-1.5" data-testid={`job-address-${j.id}`}>
          <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span className="line-clamp-2">{j.address}</span>
        </p>
      )}
      {j.description && (
        <p className="text-sm text-foreground/80 mt-2 line-clamp-3" data-testid={`job-desc-${j.id}`}>{j.description}</p>
      )}
      {j.worker_notes && (
        <p className="text-xs text-muted-foreground mt-1.5 flex items-start gap-1.5 line-clamp-3" data-testid={`job-notes-${j.id}`}>
          <StickyNote className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span className="line-clamp-3">{j.worker_notes}</span>
        </p>
      )}
      <div className="flex items-center gap-2 mt-4 text-xs">
        <span className="inline-flex items-center gap-1 text-muted-foreground"><Clock className="w-3.5 h-3.5" /> {fmtDuration(j.total_seconds)}</span>
        <span className="inline-flex items-center gap-1 text-muted-foreground"><Camera className="w-3.5 h-3.5" /> {j.photo_count}</span>
      </div>
      <div className="mt-3 pt-3 border-t border-border flex items-center gap-1.5 text-xs">
        <UserCheck className="w-3.5 h-3.5 text-primary" />
        <span className="font-medium">{j.assigned_name || j.assigned_email || "Pending assignment"}</span>
        {j.team_count > 0 && (
          <span data-testid={`job-team-count-${j.id}`} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 font-medium" title={(j.team_names || []).join(", ")}>
            <Users className="w-3 h-3" /> +{j.team_count}
          </span>
        )}
      </div>
      {/* Quick reschedule — pick a date without opening the full editor */}
      <div className="mt-3 flex items-center gap-2" data-testid={`card-reschedule-row-${j.id}`}
        onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <CalendarDays className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <span className="text-xs text-muted-foreground">Reschedule</span>
        <input
          type="date"
          data-testid={`card-reschedule-${j.id}`}
          value={j.scheduled_date || ""}
          min={todayStr}
          disabled={reschedulingId === j.id}
          onChange={(e) => quickReschedule(j, e.target.value)}
          className="text-xs bg-transparent border border-border rounded-md px-2 py-1 outline-none focus:border-primary disabled:opacity-50"
        />
        {j.scheduled_date && reschedulingId !== j.id && (
          <button type="button" data-testid={`card-clear-date-${j.id}`} onClick={() => quickReschedule(j, "")} className="text-xs text-muted-foreground hover:text-destructive">Clear</button>
        )}
        {reschedulingId === j.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />}
      </div>
      {j.clock_gap && <p className="mt-2 text-[11px] text-amber-600">⚠ On-site with no matching clock-on</p>}
      {gpsFlags[j.id] != null && (
        <p data-testid={`offsite-flag-${j.id}`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-500/10 rounded-full px-2 py-0.5">
          <AlertTriangle className="w-3 h-3" /> Off-site check-in · {gpsFlags[j.id]} m away
        </p>
      )}
    </div>
  );

  return (
    <div className="fade-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold">Jobs</h2>
          <p className="text-sm text-muted-foreground">{jobs.length} job{jobs.length !== 1 ? "s" : ""} dispatched</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-job-btn" className="rounded-full bg-primary hover:bg-primary/90 text-white">
              <Plus className="w-4 h-4 mr-2" /> New Job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-xl">Create & assign job</DialogTitle><DialogDescription>Add a new job and dispatch it to a crew member.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <DepotPicker value={form.depot_id} onChange={(v) => setForm((f) => ({ ...f, depot_id: v, client: "" }))} testId="job-depot-select" />
              <div className="space-y-2"><Label>Job name *</Label>
                <Input data-testid="job-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Venetian feature wall" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Client</Label>
                  <Select value={form.client} onValueChange={(v) => set("client", v)}>
                    <SelectTrigger data-testid="job-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
                    <SelectContent>
                      {formClients.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">{needsPick && !form.depot_id ? "Choose a depot first" : "Add clients in the Clients tab first"}</div>}
                      {formClients.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2"><Label>Job type</Label>
                    <button type="button" onClick={() => setTypesOpen(true)} className="text-xs text-primary hover:underline" data-testid="job-type-edit-list">Edit list</button></div>
                  <Select value={formType} onValueChange={(v) => set("job_type", v)}>
                    <SelectTrigger data-testid="job-type-select"><SelectValue placeholder="Pick a type" /></SelectTrigger>
                    <SelectContent>
                      {types.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`job-type-option-${t.id}`}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              </div>
              <div className="space-y-2"><Label>Site address</Label>
                <AddressAutocomplete testId="job-address-input" value={form.address} onChange={(v) => set("address", v)} placeholder="Start typing an address…" /></div>
              <div className="space-y-2"><Label>Assign to worker *</Label>
                <Select value={form.worker_email} onValueChange={(v) => set("worker_email", v)}>
                  <SelectTrigger data-testid="job-worker-select"><SelectValue placeholder="Select a worker" /></SelectTrigger>
                  <SelectContent>
                    {workers.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Add workers first</div>}
                    {workers.map((w) => <SelectItem key={w.email} value={w.email}>{w.name || w.email}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Additional workers</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="job-team-chips">
                  {team.length === 0 && <span className="text-xs text-muted-foreground">Optional — add extra crew to this job.</span>}
                  {team.map((m) => (
                    <span key={m.email} data-testid={`job-team-chip-${m.email}`} className="inline-flex items-center gap-1.5 bg-secondary rounded-full pl-3 pr-1.5 py-1 text-xs">
                      {m.name || m.email}
                      <button type="button" data-testid={`job-team-remove-${m.email}`} onClick={() => removeTeamMember(m.email)} className="rounded-full hover:bg-destructive/10 text-destructive w-4 h-4 inline-flex items-center justify-center">×</button>
                    </span>
                  ))}
                </div>
                <Select value="" onValueChange={addTeamMember}>
                  <SelectTrigger data-testid="job-team-select" className="h-9"><SelectValue placeholder="Add a team member" /></SelectTrigger>
                  <SelectContent>
                    {workers.filter((w) => w.email !== form.worker_email && !team.some((m) => m.email === w.email)).map((w) => (
                      <SelectItem key={w.email} value={w.email}>{w.name || w.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Description</Label>
                <Textarea data-testid="job-desc-input" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Scope, materials, access notes…" /></div>
              <div className="space-y-2"><Label>Scheduled date (optional)</Label>
                <Input type="date" data-testid="job-schedule-date" value={form.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} min={todayLocal()} />
                <p className="text-xs text-muted-foreground">Leave blank to dispatch now. Pick a future date and the worker won't see it until that day.</p></div>
            </div>
            <DialogFooter>
              <Button data-testid="job-create-submit" onClick={create} className="rounded-full bg-primary hover:bg-primary/90 text-white">{form.scheduled_date && form.scheduled_date > todayLocal() ? "Schedule job" : "Create job"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {scheduled.length > 0 && (
        <div className="mb-6 rounded-xl border border-amber-300/60 bg-amber-50 dark:bg-amber-950/20 p-4" data-testid="scheduled-jobs-banner">
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2">Scheduled jobs ({scheduled.length})</p>
          <div className="space-y-1.5">
            {scheduled.map((s) => (
              <div key={s.id} data-testid={`scheduled-job-${s.id}`} className="flex items-center justify-between text-sm">
                <span>{s.label} <span className="text-muted-foreground">— goes live {s.show_from}</span></span>
                <Button variant="ghost" size="sm" data-testid={`cancel-scheduled-${s.id}`} onClick={() => cancelScheduled(s.id)} className="h-7 text-destructive hover:bg-destructive/10 rounded-full">Cancel</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-6" data-testid="jobs-subtabs">
        <button data-testid="jobs-subtab-active" onClick={() => setSubTab("active")} className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${subTab === "active" ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>Active ({activeJobs.length})</button>
        <button data-testid="jobs-subtab-completed" onClick={() => setSubTab("completed")} className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${subTab === "completed" ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>Completed ({completedJobs.length})</button>
      </div>

      {subTab === "active" ? (
        activeJobs.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-2xl">
            <p className="text-muted-foreground">No active jobs. Create your first job to dispatch a worker.</p>
          </div>
        ) : (
          <>
            {upcoming.length > 0 && (
              <section className="mb-8" data-testid="upcoming-jobs-section">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarDays className="w-4 h-4 text-amber-600" />
                  <h3 className="font-display text-lg font-semibold">Scheduled / upcoming</h3>
                  <span className="text-xs text-muted-foreground">({upcoming.length})</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {upcoming.map(renderJobCard)}
                </div>
              </section>
            )}
            {current.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {current.map(renderJobCard)}
              </div>
            )}
          </>
        )
      ) : (
        <>
          <div className="mb-5 space-y-3">
            <div className="relative max-w-md">
              <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="jobs-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by job name, client or type…" className="pl-9 rounded-full" />
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Client</Label>
                <Select value={clientFilter} onValueChange={setClientFilter}>
                  <SelectTrigger data-testid="completed-client-filter" className="w-[190px] rounded-full"><SelectValue placeholder="All clients" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All clients</SelectItem>
                    {completedClients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Input type="date" data-testid="completed-from-date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} className="w-[150px] rounded-full" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" data-testid="completed-to-date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} className="w-[150px] rounded-full" />
              </div>
              {filtersActive && (
                <Button variant="ghost" size="sm" data-testid="completed-clear-filters" onClick={clearFilters} className="rounded-full text-muted-foreground">Clear filters</Button>
              )}
              <span className="text-xs text-muted-foreground ml-auto" data-testid="completed-count">{completedFiltered.length} of {completedJobs.length}</span>
            </div>
          </div>
          {completedFiltered.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-2xl">
              <p className="text-muted-foreground">{completedJobs.length === 0 ? "No completed jobs yet." : "No completed jobs match your filters."}</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-2xl overflow-x-auto" data-testid="completed-jobs-list">
              <Table className="min-w-[940px]">
                <TableHeader>
                  <TableRow>
                    {sortHead("Job", "job")}
                    {sortHead("Client", "client")}
                    {sortHead("Address", "address")}
                    {sortHead("Assigned", "assigned")}
                    {sortHead("Completed", "completed")}
                    {sortHead("Time", "time", "right")}
                    {sortHead("Photos", "photos", "right")}
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {completedSorted.map((j) => (
                    <TableRow
                      key={j.id}
                      data-testid={`completed-row-${j.id}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelected(j)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(j); } }}
                      className="cursor-pointer hover:bg-secondary/50"
                    >
                      <TableCell className="font-medium">
                        {j.name}
                        <div className="text-xs font-normal text-muted-foreground">{jobTypeLabel(j)}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 shrink-0" /> {j.client || "—"}</span>
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-[240px]">
                        <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 shrink-0" /> <span className="line-clamp-1">{j.address || "—"}</span></span>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 text-sm"><UserCheck className="w-3.5 h-3.5 text-primary shrink-0" /> <span className="font-medium">{j.assigned_name || j.assigned_email || "—"}</span>
                          {j.team_count > 0 && <span className="text-xs text-primary font-medium">+{j.team_count}</span>}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap text-sm">{fmtDate(j.completed_at || j.updated_at)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtDuration(j.total_seconds)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{j.photo_count}</TableCell>
                      <TableCell><JobBadge job={j} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}

      {selected && <JobDetailDialog job={selected} workers={workers} clients={clients} onClose={() => setSelected(null)} onChanged={load} />}
      <JobTypesDialog open={typesOpen} onOpenChange={setTypesOpen} />
    </div>
  );
};

const JobDetailDialog = ({ job, workers, clients = [], onClose, onChanged }) => {
  const [details, setDetails] = useState(null);
  const [entries, setEntries] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [reassign, setReassign] = useState("");
  const [team, setTeam] = useState([]);
  const [gps, setGps] = useState([]);
  const [sent, setSent] = useState([]);
  const [emailing, setEmailing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [editing, setEditing] = useState(false);
  const [ef, setEf] = useState({ name: "", client: "", address: "", description: "", job_type: "standard", scheduled_date: "" });
  const setE = (k, v) => setEf((f) => ({ ...f, [k]: v }));
  const [presets, setPresets] = useState({ products: [], lengths: [] });
  const [removedText, setRemovedText] = useState("");
  const [savingItems, setSavingItems] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(null);
  const companyTypes = useCompany().company?.job_types || [];
  const loadInvoice = () => api.invoices({ job_id: job.id }).then((r) => setInvoice(r.invoices.find((i) => i.status !== "void") || null)).catch(() => {});

  useEffect(() => {
    loadInvoice();
    (async () => {
      try {
        const [d, e, p, t, g, s, pr, ln] = await Promise.all([api.job(job.id), api.jobEntries(job.id), api.jobPhotos(job.id), api.jobTeam(job.id).catch(() => []), api.jobGpsLog(job.id).catch(() => []), api.reportsSent(job.id).catch(() => []), api.products().catch(() => []), api.lengths().catch(() => [])]);
        setDetails(d); setEntries(e); setPhotos(p); setTeam(t || []); setGps(g || []); setSent(s || []); setPresets({ products: pr || [], lengths: ln || [] }); setRemovedText(d.remarks || "");
      } catch { toast.error("Failed to load job details"); }
    })();
  }, [job.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const emailReport = async () => {
    setEmailing(true);
    try {
      const r = await api.emailJobReport(job.id);
      if (r.sent) { toast.success(`Report emailed to ${r.to_email}`); setSent((prev) => [r.log, ...prev]); }
      else { toast.info(r.reason || "Report was not sent"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send report"); }
    finally { setEmailing(false); }
  };

  const saveTeam = async (members) => {
    setTeam(members);
    try { await api.setJobTeam(job.id, members); } catch { toast.error("Could not update team"); }
  };
  const addMember = (email) => {
    const w = workers.find((x) => x.email === email);
    if (!w || team.some((m) => m.email === email)) return;
    saveTeam([...team, { email: w.email, name: w.name || w.email }]);
    api.notifyAssignment(w.email, (details || job).name, job.id).catch(() => {});
    toast.success(`${w.name || w.email} added to the job`);
  };
  const removeMember = (email) => saveTeam(team.filter((m) => m.email !== email));

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChanged(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const startEdit = () => {
    const d0 = details || job;
    setEf({
      name: d0.name || "",
      client: d0.client || "",
      address: d0.address || "",
      description: d0.description || "",
      job_type: d0.job_type || "standard",
      scheduled_date: d0.scheduled_date || "",
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    if (!ef.name.trim()) { toast.error("Job name is required"); return; }
    setBusy(true);
    try {
      const prevDate = (details || job).scheduled_date || "";
      const dateChanged = prevDate !== (ef.scheduled_date || "");
      const updated = await api.updateJob(job.id, {
        name: ef.name.trim(), client: ef.client, address: ef.address,
        description: ef.description, job_type: ef.job_type,
        scheduled_date: ef.scheduled_date || "",
      });
      setDetails(updated); setEditing(false); onChanged();
      // Notify the assigned worker + team that the job details changed.
      const recipients = new Set();
      if (updated.assigned_email) recipients.add(updated.assigned_email);
      team.forEach((m) => m.email && recipients.add(m.email));
      recipients.forEach((emailAddr) => {
        if (dateChanged && ef.scheduled_date) api.notifyJobReschedule(emailAddr, updated.name || ef.name, ef.scheduled_date, job.id).catch(() => {});
        else api.notifyJobUpdate(emailAddr, updated.name || ef.name, job.id).catch(() => {});
      });
      toast.success(dateChanged && ef.scheduled_date ? `Job rescheduled to ${fmtDate(ef.scheduled_date)}` : "Job updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save changes"); }
    finally { setBusy(false); }
  };

  const d = details || job;
  const editTypes = !ef.job_type || companyTypes.some((t) => t.id === ef.job_type)
    ? companyTypes : [...companyTypes, { id: ef.job_type, label: `${jobTypeLabel(d)} (removed)` }];

  const saveRemoved = async () => {
    setSavingItems(true);
    try {
      const updated = await api.updateJob(job.id, { remarks: removedText });
      setDetails(updated); onChanged();
      toast.success("Removed-items note saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSavingItems(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88dvh] overflow-y-auto sm:max-h-[calc(100dvh-3rem)]">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-6">
            <DialogTitle className="font-display text-xl">{d.name}</DialogTitle>
            <div className="flex items-center gap-2">
              {!editing && (
                <Button variant="outline" size="sm" data-testid="edit-job-btn" onClick={startEdit} className="rounded-full h-8">
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
              )}
              <JobBadge job={d} />
            </div>
          </div>
          <DialogDescription className="sr-only">Job details, time entries, photos and actions.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {editing ? (
            <div className="space-y-4" data-testid="job-edit-form">
              <div className="space-y-1.5"><Label className="text-xs">Job name *</Label>
                <Input data-testid="edit-job-name" value={ef.name} onChange={(e) => setE("name", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Client</Label>
                  <Select value={ef.client} onValueChange={(v) => setE("client", v)}>
                    <SelectTrigger data-testid="edit-job-client"><SelectValue placeholder="Select a client" /></SelectTrigger>
                    <SelectContent>
                      {clients.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No clients yet</div>}
                      {clients.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label className="text-xs">Job type</Label>
                  <Select value={ef.job_type} onValueChange={(v) => setE("job_type", v)}>
                    <SelectTrigger data-testid="edit-job-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {editTypes.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`edit-job-type-option-${t.id}`}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Site address</Label>
                <AddressAutocomplete testId="edit-job-address" value={ef.address} onChange={(v) => setE("address", v)} placeholder="Start typing an address…" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Description</Label>
                <Textarea data-testid="edit-job-desc" value={ef.description} onChange={(e) => setE("description", e.target.value)} placeholder="Scope, materials, access notes…" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Scheduled date</Label>
                <div className="flex items-center gap-2">
                  <Input type="date" data-testid="edit-job-scheduled-date" value={ef.scheduled_date} onChange={(e) => setE("scheduled_date", e.target.value)} className="w-auto" />
                  {ef.scheduled_date && (
                    <Button type="button" variant="ghost" size="sm" data-testid="clear-scheduled-date" onClick={() => setE("scheduled_date", "")} className="rounded-full h-8 text-muted-foreground">Clear</Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Reschedule this job. A future date hides it from the worker until that day; they'll be notified of the change.</p></div>
              <div className="flex gap-2">
                <Button data-testid="save-job-edit" disabled={busy || !ef.name.trim()} onClick={saveEdit} className="rounded-full bg-primary hover:bg-primary/90 text-white">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save changes
                </Button>
                <Button variant="outline" data-testid="cancel-job-edit" disabled={busy} onClick={() => setEditing(false)} className="rounded-full">Cancel</Button>
              </div>
            </div>
          ) : (
          <>
          <div className="grid grid-cols-2 gap-3">
            <Info label="Type" value={jobTypeLabel(d)} testId="detail-job-type" />
            <Info label="Client" value={d.client || "—"} />
            <div>
              <p className="text-xs text-muted-foreground">Address</p>
              {d.address ? (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(d.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="navigate-address-link"
                  className="font-medium text-primary inline-flex items-start gap-1.5 hover:underline"
                >
                  <Navigation className="w-4 h-4 mt-0.5 shrink-0" /> {d.address}
                </a>
              ) : (
                <p className="font-medium">—</p>
              )}
            </div>
            <Info label="Assigned" value={d.assigned_name || d.assigned_email || "Pending"} />
            {d.scheduled_date && (
              <div data-testid="detail-scheduled-date">
                <p className="text-xs text-muted-foreground">Scheduled date</p>
                <p className={`font-medium inline-flex items-center gap-1.5 ${d.scheduled_date > todayLocal() ? "text-amber-600" : ""}`}>
                  <CalendarDays className="w-4 h-4 shrink-0" /> {fmtDate(d.scheduled_date)}
                  {d.scheduled_date > todayLocal() && <span className="text-xs opacity-70">· upcoming</span>}
                </p>
              </div>
            )}
            <Info label="Total time" value={fmtDuration(d.total_seconds)} />
            <Info label="On-site time" value={d.time_on_site_seconds != null ? fmtDuration(d.time_on_site_seconds) : "—"} />
            {d.arrived_on_scene_at && <Info label="Arrived on site" value={fmtDateTime(d.arrived_on_scene_at)} />}
            {d.left_scene_at && <Info label="Left site" value={fmtDateTime(d.left_scene_at)} />}
          </div>
          {d.description && <div><p className="text-xs text-muted-foreground mb-1">Description</p><p>{d.description}</p></div>}
          </>
          )}
          {d.worker_notes && <div><p className="text-xs text-muted-foreground mb-1">Worker notes</p><p className="whitespace-pre-wrap">{d.worker_notes}</p></div>}

          {jobKind(d) === "site_clean" && (
            <div className="border-t border-border pt-3" data-testid="removed-items-section">
              <p className="text-xs text-muted-foreground mb-2">Items removed</p>
              <Textarea
                data-testid="removed-items-textarea"
                value={removedText}
                onChange={(e) => setRemovedText(e.target.value)}
                placeholder="List what was removed from the site…"
                className="min-h-[110px]"
              />
              <Button size="sm" data-testid="save-removed-items" disabled={savingItems} onClick={saveRemoved} className="rounded-full h-8 text-xs mt-2 bg-primary hover:bg-primary/90 text-white">
                {savingItems ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null} Save items
              </Button>
              {(d.removed_items || []).length > 0 && (
                <div className="overflow-hidden rounded-lg border border-border mt-3">
                  <table className="w-full text-xs">
                    <thead className="bg-secondary/60 text-muted-foreground">
                      <tr><th className="text-left font-medium px-3 py-2">Qty</th><th className="text-left font-medium px-3 py-2">Size</th><th className="text-left font-medium px-3 py-2">Length</th></tr>
                    </thead>
                    <tbody>
                      {(d.removed_items || []).map((it, i) => (
                        <tr key={i} data-testid={`removed-item-${i}`} className="border-t border-border">
                          <td className="px-3 py-2 font-mono">{it.quantity || "\u2014"}</td>
                          <td className="px-3 py-2">{it.size || "\u2014"}</td>
                          <td className="px-3 py-2">{it.length || "\u2014"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-[11px] text-muted-foreground px-3 py-2 bg-secondary/30">Earlier itemised list (read-only). New notes are saved above.</p>
                </div>
              )}
            </div>
          )}

          <div className="border-t border-border pt-3" data-testid="job-team-section">
            <p className="text-xs text-muted-foreground mb-2">Team members</p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {team.length === 0 && <span className="text-muted-foreground text-xs">No extra team members.</span>}
              {team.map((m) => (
                <span key={m.email} data-testid={`team-member-${m.email}`} className="inline-flex items-center gap-1.5 bg-secondary rounded-full pl-3 pr-1.5 py-1 text-xs">
                  {m.name || m.email}
                  <button data-testid={`remove-team-${m.email}`} onClick={() => removeMember(m.email)} className="rounded-full hover:bg-destructive/10 text-destructive w-4 h-4 inline-flex items-center justify-center">×</button>
                </span>
              ))}
            </div>
            <Select value="" onValueChange={addMember}>
              <SelectTrigger data-testid="add-team-select" className="h-9"><SelectValue placeholder="Add a team member" /></SelectTrigger>
              <SelectContent>
                {workers.filter((w) => !team.some((m) => m.email === w.email)).map((w) => (
                  <SelectItem key={w.email} value={w.email}>{w.name || w.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border-t border-border pt-3" data-testid="gps-log-section">
            <p className="text-xs text-muted-foreground mb-2">GPS check-in / out <span className="text-[11px]">(cross-reference vs site)</span></p>
            {gps.length === 0 ? (
              <p className="text-muted-foreground text-xs">No GPS captured yet — recorded when a worker checks in/out from their phone.</p>
            ) : (
              <div className="space-y-1.5">
                {gps.map((x) => {
                  const dist = x.distance_m;
                  const flagged = !!x.flagged;
                  return (
                    <div key={x.id} data-testid={`gps-${x.id}`} className={`flex items-center justify-between gap-2 text-xs rounded px-3 py-1.5 ${flagged ? "bg-red-500/10 border border-red-500/30" : "bg-secondary/50"}`}>
                      <span className="inline-flex items-center gap-1.5 min-w-0">
                        <span className={`px-1.5 py-0.5 rounded-full font-medium ${x.type === "in" ? "bg-emerald-500/15 text-emerald-600" : "bg-stone-500/15 text-stone-500"}`}>{x.type === "in" ? "In" : "Out"}</span>
                        <span className="truncate">{x.user_name} · {fmtDateTime(x.at)}</span>
                      </span>
                      {x.available ? (
                        <span className="inline-flex items-center gap-2 shrink-0">
                          {flagged
                            ? <span className="inline-flex items-center gap-1 text-red-600 font-semibold"><AlertTriangle className="w-3 h-3" /> {dist} m off-site</span>
                            : dist != null && <span className="text-emerald-600">{dist} m from site</span>}
                          <a href={`https://www.google.com/maps?q=${x.lat},${x.lng}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Map</a>
                        </span>
                      ) : <span className="text-muted-foreground shrink-0">location unavailable</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="border-t border-border pt-3">
            <p className="text-xs text-muted-foreground mb-2">Time entries ({entries.length})</p>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {entries.length === 0 && <p className="text-muted-foreground text-xs">No time logged yet.</p>}
              {entries.map((e) => (
                <div key={e.id} className="flex justify-between text-xs bg-secondary/50 rounded px-3 py-1.5">
                  <span>{fmtDateTime(e.started_at)}</span>
                  <span className="font-mono">{e.duration_seconds ? fmtDuration(e.duration_seconds) : "in progress"}</span>
                </div>
              ))}
            </div>
          </div>

          {photos.length > 0 && (
            <div className="border-t border-border pt-3">
              <p className="text-xs text-muted-foreground mb-2">Photos ({photos.length})</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {photos.map((p, i) => (
                  <div key={p.id} className="space-y-1">
                    <button type="button" data-testid={`job-photo-${p.id}`} onClick={() => setLightbox(i)} className="relative group block w-full">
                      <img src={photoSrc(p.image_base64)} alt={p.kind} className="rounded-lg aspect-square object-cover border border-border w-full transition-transform group-hover:scale-[1.03]" />
                      <span className="absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 rounded bg-black/60 text-white capitalize">{p.kind}</span>
                    </button>
                    {p.note && <p className="text-[11px] text-muted-foreground leading-snug" data-testid={`job-photo-note-${p.id}`}>{p.note}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-border pt-4 space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Reassign worker</Label>
                <Select value={reassign} onValueChange={setReassign}>
                  <SelectTrigger data-testid="reassign-select"><SelectValue placeholder="Choose worker" /></SelectTrigger>
                  <SelectContent>
                    {workers.map((w) => <SelectItem key={w.email} value={w.email}>{w.name || w.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" disabled={!reassign || busy} data-testid="reassign-btn"
                onClick={() => act(async () => { await api.updateJob(job.id, { worker_email: reassign }); api.notifyAssignment(reassign, d.name).catch(() => {}); }, "Job reassigned")} className="rounded-full">
                Reassign
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {d.status !== "completed" ? (
                <Button data-testid="complete-job-btn" disabled={busy} onClick={() => act(async () => { await api.updateJob(job.id, { status: "completed" }); api.onJobComplete(job.id).catch(() => {}); }, "Job completed")}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Mark complete
                </Button>
              ) : (
                <Button data-testid="reopen-job-btn" variant="outline" disabled={busy} onClick={() => act(() => api.updateJob(job.id, { status: "active" }), "Job reopened")} className="rounded-full">
                  <RotateCcw className="w-4 h-4 mr-2" /> Reopen
                </Button>
              )}
              <Button data-testid="job-report-pdf-btn" variant="outline" disabled={busy}
                onClick={async () => { try { await openJobReport(job.id); } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Could not open PDF"); } }}
                className="rounded-full">
                <FileText className="w-4 h-4 mr-2" /> Report PDF
              </Button>
              <Button data-testid="email-report-btn" variant="outline" disabled={emailing}
                onClick={emailReport} className="rounded-full">
                {emailing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Email to client
              </Button>
              {d.status === "completed" && (invoice ? (
                <Button data-testid="job-open-invoice-btn" variant="outline" onClick={() => setInvoiceOpen(invoice.id)} className="rounded-full">
                  <Receipt className="w-4 h-4 mr-2" /> {invoice.number || "Draft invoice"} <span className="ml-2"><InvoiceStatusBadge status={invoice.display_status} /></span>
                </Button>
              ) : (
                <Button data-testid="job-create-invoice-btn" disabled={busy} onClick={async () => {
                  setBusy(true);
                  try { const inv = await api.createInvoice(job.id); setInvoice(inv); setInvoiceOpen(inv.id); }
                  catch (e) { toast.error(e?.response?.data?.detail || "Could not create invoice"); }
                  finally { setBusy(false); }
                }} className="rounded-full bg-primary hover:bg-primary/90 text-white">
                  <Receipt className="w-4 h-4 mr-2" /> Create invoice
                </Button>
              ))}
              <Button data-testid="delete-job-btn" variant="ghost" disabled={busy}
                onClick={() => { if (window.confirm(`Delete "${d.name}"? This removes it for the whole crew and cannot be undone.`)) act(async () => {
                  const recips = new Set();
                  if (d.assigned_email) recips.add(d.assigned_email);
                  team.forEach((m) => m.email && recips.add(m.email));
                  await api.deleteJob(job.id);
                  recips.forEach((emailAddr) => api.notifyJobRemoved(emailAddr, d.name).catch(() => {}));
                }, "Job deleted"); }} className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            </div>
            {sent.length > 0 && (
              <p data-testid="report-last-sent" className="mt-3 text-xs text-emerald-600 inline-flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Report emailed to {sent[0].to_email} · {fmtDateTime(sent[0].at)}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
      <PhotoLightbox photos={photos} index={lightbox} onClose={() => setLightbox(null)} onIndexChange={setLightbox} />
      <InvoiceDialog invoiceId={invoiceOpen} onClose={() => setInvoiceOpen(null)} onChanged={loadInvoice} />
    </Dialog>
  );
};

const Info = ({ label, value, testId }) => (
  <div data-testid={testId}><p className="text-xs text-muted-foreground">{label}</p><p className="font-medium">{value}</p></div>
);
